export interface MatchFilters {
  partyId?: string;
  productId?: string;
}

export interface MatchListRow {
  id: string;
  partyId: string;
  partyName: string;
  terms: string[] | null;
  productId: string;
  productVariantId: string;
  productName: string;
  variantName: string | null;
  variantSku: string | null;
  updatedAt: Date | string;
}

export interface MatchListResult {
  rows: MatchListRow[];
  pagination?: { total: number; limit: number; offset: number };
  isReady: boolean;
}

export interface PageState {
  pageIndex: number;
  pageSize: number;
}

export interface ListQuery extends MatchFilters {
  limit: number;
  offset: number;
}

export type MatchModalState<T> =
  { mode: 'create' } | { mode: 'upload' } | { mode: 'edit'; match: T } | null;

const BLANK_FILTER_TOKENS = new Set(['', 'undefined', 'null']);

export function normalizeFilterValue(
  value?: string | null,
): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (BLANK_FILTER_TOKENS.has(trimmed)) return undefined;
  return trimmed;
}

export function readMatchFilters(
  partyId: string | undefined,
  productId: string | undefined,
  fallbackPartyId: string | null,
  fallbackProductId: string | null,
): MatchFilters {
  return {
    partyId: normalizeFilterValue(partyId ?? fallbackPartyId),
    productId: normalizeFilterValue(productId ?? fallbackProductId),
  };
}

export function canLoadMatches(filters: MatchFilters): boolean {
  return Boolean(filters.partyId || filters.productId);
}

export function listQuery(filters: MatchFilters, page: PageState): ListQuery {
  const query: ListQuery = {
    limit: page.pageSize,
    offset: page.pageIndex * page.pageSize,
  };
  if (filters.partyId) query.partyId = filters.partyId;
  if (filters.productId) query.productId = filters.productId;
  return query;
}

export function pageCount(total: number, limit: number): number {
  if (limit <= 0) return 0;
  return Math.ceil(total / limit);
}

export function showingRange(pagination: {
  offset: number;
  limit: number;
  total: number;
}): { start: number; end: number; total: number } {
  return {
    start: pagination.offset + 1,
    end: Math.min(pagination.offset + pagination.limit, pagination.total),
    total: pagination.total,
  };
}

export function resetPage(page: PageState): PageState {
  return { pageIndex: 0, pageSize: page.pageSize };
}

export function pageForFilters(
  previous: MatchFilters,
  next: MatchFilters,
  page: PageState,
): PageState {
  if (
    previous.partyId === next.partyId &&
    previous.productId === next.productId
  ) {
    return page;
  }
  if (page.pageIndex === 0) return page;
  return resetPage(page);
}

export function editMatch<T>(modal: MatchModalState<T>): T | undefined {
  if (modal?.mode === 'edit') return modal.match;
  return undefined;
}
